import {
	CheckboxChecked,
	CheckboxUnchecked,
	EnvelopeGrey
} from '@/resources/assets/images';
import type { RootState } from '@/rootReducer';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { Tooltip } from '@/components/atoms/Tooltip';
import { shallowEqual, useSelector } from 'react-redux';

import './ScheduledEmailsFilter.scss';
import { UserRole } from '@/modules/user/userTypes';

type ScheduledEmailsBodyProps = {
	selectedEmails: string[];
	setSelectedEmails: React.Dispatch<React.SetStateAction<string[]>>;
};

const ScheduledEmailsBody = ({
	selectedEmails,
	setSelectedEmails
}: ScheduledEmailsBodyProps): JSX.Element => {
	const [scheduledEmails, user] = useSelector(
		(state: RootState) => [
			state.email.getIn(['email', 'scheduled', 'emails']),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);
	const checkboxClickHandler = (businessId: string) => {
		if (selectedEmails.includes(businessId)) {
			setSelectedEmails(prev => prev.filter(id => id !== businessId));
		} else {
			setSelectedEmails(prev => [...prev, businessId]);
		}
	};

	const isUserFreeTier =
		user.role === UserRole.Free || user.role === UserRole.PreTrial;

	return (
		<div
			className={`scheduled-emails-list-item-holder-redesign ${
				scheduledEmails.length === 0 ? 'no-emails' : ''
			}`}
		>
			{scheduledEmails.map(email => (
				<div
					key={email._id}
					className="scheduled-emails-list-item-redesign"
				>
					<button
						type="button"
						className="scheduled-emails-list-item-checkbox"
						onClick={() => checkboxClickHandler(email.businessId)}
					>
						{selectedEmails.includes(email.businessId) ? (
							<CheckboxChecked />
						) : (
							<CheckboxUnchecked />
						)}
					</button>
					<span className="scheduled-emails-list-item-name">
						{email.name}
					</span>
					<Tooltip.Provider>
						<Tooltip.Root>
							<Tooltip.Trigger asChild>
								<span
									data-freetier={isUserFreeTier}
									className="scheduled-emails-list-item-email-address data-[freetier=true]:select-none data-[freetier=true]:blur-sm"
								>
									{email.email}
								</span>
							</Tooltip.Trigger>
							<Tooltip.Content disabled={!isUserFreeTier}>
								Upgrade to a paid user to uncover email
								addresses
							</Tooltip.Content>
						</Tooltip.Root>
					</Tooltip.Provider>
					<span className="scheduled-emails-list-item-email-content-redesign">
						<span className="scheduled-emails-list-item-email-subject">
							{email.subject}
						</span>
						<div
							className="scheduled-emails-list-item-email-body"
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(email.body)
							}}
						/>
					</span>
					<span className="scheduled-emails-list-item-scheduled-date">
						{` ${dayjs(email.scheduleDate)?.format('ddd, MMM D')} at ${dayjs(email.scheduleDate)?.format('hh:mm a')}`}
					</span>
				</div>
			))}
			{scheduledEmails?.length === 0 && (
				<div className="scheduled-emails-empty-div">
					<EnvelopeGrey className="communication-center-empty-icon" />
					<span className="communication-center-empty-text">
						No scheduled emails found. Try changing your date range
						in order to find emails.
					</span>
				</div>
			)}
		</div>
	);
};

export default ScheduledEmailsBody;
